// Voorsorteren op de samenvoeglaag (PR #28, tak samenvoegen-lijst, nog niet
// gemerged). Daar krijgt de API, als hij draait met SOF_SAMENVOEGLAAG, extra
// velden: `samenvoeging` op een trend, `kaart` op een groep en `samenvoeging`
// op het lijstantwoord, plus `samenvoeglaag` in /api/meta. Op main bestaan ze
// niet. Hier staan ze als OPTIONELE, lokale types (contracts/ blijft
// onaangeroerd), met kleine lezers die `null` geven zolang de API niets levert.
// Het scherm toont ze alleen als ze er zijn; anders verandert er niets.
//
// Wat we bewust NIET doen: GET /api/trends/ontwikkeling/{sleutel} aanroepen.
// Die route bestaat op main niet. Een ontwikkeling openen we via haar
// kop-trend; de andere leden staan al als `children` in de lijst.

import type { Toon } from "../../stijl";

export type SamenvoegLabel = {
  tekst: string | null;
  soort: "advies" | "bevestigd" | "gesignaleerd" | null;
  uitleg: string;
  organisaties: number | null;
  regel: string | null;
  telt: boolean;
};

export type SamenvoegOnderwerp = {
  sleutel: string;
  naam: string;
  hoofdonderwerp?: string;
  gebied?: { naam: string; kleur: string | null };
};

export type SamenvoegHoortBij = {
  sleutel: string;
  titel: string;
  label: SamenvoegLabel;
  nTrends: number;
  organisaties: number;
  werkzin?: string | null;
  uitleg?: string | null;
  onderwerp?: SamenvoegOnderwerp | null;
  kopTrendId?: string | null;
};

/** Op een trend: waar hoort hij bij (alleen met de laag aan). */
export type TrendSamenvoeging = {
  proef: boolean;
  soort: "ontwikkeling" | "los" | "buiten";
  onderwerp?: SamenvoegOnderwerp | null;
  ontwikkeling?: { sleutel: string; titel: string; nLeden: number };
  reden?: string | null;
  hoortBij?: SamenvoegHoortBij | null;
  label?: SamenvoegLabel;
};

export type SamenvoegBewijs = {
  bronnen: number;
  organisaties: number;
  perSoort: { soort: string; label: string; bronnen: number; organisaties: number }[];
  nieuwsteBron: string | null;
  trendsGeteld: number;
  vanTrends?: number;
};

/** Op een groep in de samengevoegde lijst: wat de kaart toont. */
export type SamenvoegKaart = {
  soort: "ontwikkeling" | "los" | "buiten";
  sleutel: string;
  titel: string;
  uitleg?: string | null;
  onderwerp?: SamenvoegOnderwerp | null;
  nLeden: number;
  nInSelectie: number;
  bronnen: number;
  organisaties?: number | null;
  werkzin?: string | null;
  thema?: { sleutel: string; titel: string; tussenkop: boolean } | null;
  label?: SamenvoegLabel;
  nieuw?: { nieuw: boolean; nTrends: number; maand: string | null };
  bewijs?: SamenvoegBewijs;
};

/** Op het lijstantwoord: de tellingen van de samengevoegde lijst. */
export type SamenvoegLijst = {
  proef: boolean;
  label: string;
  trends: number;
  kaarten: number;
  ontwikkelingen: number;
  losseSignalen: number;
  buiten: number;
  bevestigdOfAdvies: number;
};

/** In /api/meta: is de laag er? */
export type SamenvoegMeta = { actief: boolean; label: string; standaardWeergave?: "nu" | "samengevoegd" };

const obj = (v: unknown): Record<string, unknown> | null => (v && typeof v === "object" ? (v as Record<string, unknown>) : null);

/** `meta.samenvoeglaag` als de API hem meldt, anders null (live). */
export function samenvoegMeta(meta: unknown): SamenvoegMeta | null {
  const s = obj(obj(meta)?.samenvoeglaag);
  return s && s.actief !== false ? (s as unknown as SamenvoegMeta) : null;
}

/** `trend.samenvoeging` als het er is. */
export function samenvoegingVan(trend: unknown): TrendSamenvoeging | null {
  const s = obj(obj(trend)?.samenvoeging);
  return s && typeof s.soort === "string" ? (s as unknown as TrendSamenvoeging) : null;
}

/** `groep.kaart` als het er is. */
export function kaartVan(groep: unknown): SamenvoegKaart | null {
  const k = obj(obj(groep)?.kaart);
  return k && typeof k.titel === "string" ? (k as unknown as SamenvoegKaart) : null;
}

/** `lijst.samenvoeging` als het er is. */
export function lijstSamenvoeging(lijst: unknown): SamenvoegLijst | null {
  const s = obj(obj(lijst)?.samenvoeging);
  return s && typeof s.kaarten === "number" ? (s as unknown as SamenvoegLijst) : null;
}

/** Het label van de laag in de toon van de stijllaag; null = geen label. */
export function samenvoegToon(soort: SamenvoegLabel["soort"]): Toon {
  return soort === "bevestigd" ? "groen" : soort === "advies" ? "blauw" : "neutraal";
}
